import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import '../../css/Booking/guestPayment.css'
import CommonModal from '../../components/Common-components/Modal/modal';
import { apiGet, apiPost } from '../../utils/api';
import { PAYMENT_REVIEW_MESSAGE, shouldBlockPaymentRetry } from '../../utils/paymentRetrySafety';
import Accordion from 'react-bootstrap/Accordion';
import creditCardType from 'credit-card-type'; // 카드 타입 조회 및 유효성 검사 라이브러리
// import { bookingOptionData } from './bookingDummyData'; // bookingOptionData import (옵션 선택 비활성화로 주석 처리)
import InfoButton from '../../components/Common-components/infoButton';
import { DEFAULT_TERMS, resolveTermsByLineId } from './termsData';
import CancellationTermsAgreement from '../../components/Common-components/CancellationTermsAgreement';
import useCancellationAgreement from '../../hooks/useCancellationAgreement';
import { resolveCautionByLineId } from '../../utils/cautionData';
import { buildBookingBasketCacheKey, resolveBookingResultNo } from '../../utils/traveltekBookingApi';
import {
    findSelectedTraveltekGratuity,
    buildTraveltekPriceTranslations,
    getTraveltekPriceLabel,
    findTraveltekGratuityExtra,
    isTraveltekGratuitySelected,
    isTraveltekGratuityPriceItem,
    normalizeTraveltekPassengerPriceItem,
} from '../../utils/traveltekPriceBreakdown';
import { PromotionDiscountRows } from '../../utils/promotionSummary';
import { resolveTraveltekPassengers } from '../../utils/traveltekPassengerMapping';
import useActiveEtcCodes from '../../hooks/useActiveEtcCodes';
// svg
import { ReactComponent as DotIcon } from '../../assets/icons/svg/dot.svg'
import { ReactComponent as ArrowCircleIcon } from '../../assets/icons/svg/arrowCircle.svg';
import { ReactComponent as VisaIcon } from '../../assets/icons/svg/logo_visa.svg'
import { ReactComponent as MasterIcon } from '../../assets/icons/svg/logo_master_card.svg'
import { ReactComponent as AmexIcon } from '../../assets/icons/svg/logo_amex.svg'
import Loading from '../../components/Common-components/loading';

const DEFAULT_PASSENGER_LABELS = {
    adults: '성인',
    children: '소아',
};

const PASSENGER_TYPE_ORDER = ['adults', 'children'];
const PARTIAL_PAYMENT_LINE_IDS = [
    17,
    22,
    73,
];

function isPartialPaymentLine(lineId) {
    return PARTIAL_PAYMENT_LINE_IDS.includes(Number(lineId));
}

function formatUsdCurrency(value) {
    return Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function parseMoneyValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

function formatDueDateStatus(value) {
    if (!value) return '-';
    return `${String(value).slice(0, 10)}까지`;
}

function normalizePassengerCounts(counts) {
    if (!counts || typeof counts !== 'object') {
        return { adults: 0, children: 0 };
    }
    const adults = Number(counts.adults) || 0;
    const children = Number(counts.children) || 0;
    return { adults, children };
}

/**
 * 250916 - 결제 가능한 카드type
 * VISA (직불 타입으로 결제시, BIN/IIN 으로 조회후 타입 나눠서 진행 필요)
 * MASTER
 * AMEX
 * 타 카드type 제한중
 * 
 * 피그마로 wowcruise측에 문의 남겨둔 상황입니다.
 */

// 결제 가능한 cardType 매핑 (Traveltek 코드 기준)
// VIS: Visa Credit Card        (신용) 
// MSC: Mastercard              (신용/직불,체크 구분 X) 
// DEL: Visa Debit Card         (직불,체크)
// MAE: Maestro                 (일부 국가 한정 - 축소중)
// SWI: Switch                  (영국 전용 - 단종)
// AMX: American Express        (? 카드 번호 - 4/6/5 형식)
// ELC: Electron                (일부 국가 한정 - 축소중)
// SOL: Solo                    (단종)
const cardTypeMapping = (brand) => {
    // hyphen/space 등의 구분자 제거 후 비교 (예: american-express → americanexpress)
    const key = (brand || '').toLowerCase().replace(/[^a-z]/g, '');
    switch (key) {
        case 'visa':
            return 'VIS';
        case 'mastercard':
            return 'MSC';
        case 'americanexpress':
        case 'amex':
            return 'AMX';
        case 'maestro':
            return 'MAE';
        case 'visaelectron':
            return 'ELC';
        case 'switch':
            return 'SWI';
        case 'solo':
            return 'SOL';
        default:
            return '';
    }
};

// 결제하기 버튼 컴포넌트
const PaymentButton = ({ onClick, disabled, text }) => (
    <button
        type='button'
        onClick={onClick}
        disabled={disabled}
        className={'payment-btn btn'}
    >
        {text}
    </button>
);

// 비회원 결제 단계에서 카드 정보를 수집하고 결제 API를 호출하는 화면
function GuestPayment({ bookingPayload, onNextStep, ageConfig }) {
    const activeEtcCodes = useActiveEtcCodes();
    const priceTranslations = useMemo(() => buildTraveltekPriceTranslations(activeEtcCodes), [activeEtcCodes]);
    const { ages,carbinGradeInfo,counts: rawCounts,cruise,detailData,passengerInfo,reservationType,userSelectCabinInfo } = bookingPayload || {};
    const { idx } = useParams();
    const counts = useMemo(() => normalizePassengerCounts(rawCounts), [rawCounts]);
    const passengerLabels = useMemo(() => ({
        adults: ageConfig?.adult?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.adults,
        children: ageConfig?.child?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.children,
    }), [ageConfig]);
    const cabinName = carbinGradeInfo?.grade_name_ko || carbinGradeInfo?.grade_name;
    // 1단계에서 입력한 소아 나이
    const childAges = useMemo(() => {
        const rawChildren = ages?.children;
        if (!Array.isArray(rawChildren)) return [];
        return rawChildren.map(value => {
            if (value === null || value === undefined) return null;
            const normalized = typeof value === 'string' ? value.trim() : value;
            if (normalized === '') return null;
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : null;
        });
    }, [ages]);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 651);
    const [portalContainer, setPortalContainer] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [blockedPayments, setBlockedPayments] = useState(() => new Set());
    const paymentInFlightRef = useRef(false);
    const cancellationTermsRef = useRef(null);

    const basketData = useMemo(() => {
        const base = passengerInfo?.basketData || null;
        const gradeInfo = carbinGradeInfo;
        const selectedPricing = gradeInfo?.gridPrice;
        const resultNO = resolveBookingResultNo(gradeInfo);
        const gradeNo = selectedPricing?.gradeno ?? selectedPricing?.rate?.gradeno ?? gradeInfo?.gradeno;
        const rateCode = selectedPricing?.rate?.code ?? selectedPricing?.ratecode;
        const cabinResult = userSelectCabinInfo?.resultno;
        const cruiseId = cruise?.codeToCruiseId || cruise?.id || cruise?.code;
        if ((!resultNO && !cruiseId) || !gradeNo || !rateCode || !cabinResult) return base;
        const cacheKey = buildBookingBasketCacheKey({
            scheduleId: idx != null ? idx : null,
            cruiseId,
            resultNO,
            gradeNo,
            rateCode,
            cabinResult,
        });
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (!cached) return base;
            const parsed = JSON.parse(cached);
            if (!parsed?.results) return base;
            if (!base) return parsed;
            const basePrice = Number(base?.results?.[0]?.totalprice || 0);
            const cachedPrice = Number(parsed?.results?.[0]?.totalprice || 0);
            const basePerperson = base?.results?.[0]?.basketitems?.[0]?.cruisedetail?.perperson;
            const cachedPerperson = parsed?.results?.[0]?.basketitems?.[0]?.cruisedetail?.perperson;
            if (!Array.isArray(basePerperson) && Array.isArray(cachedPerperson)) return parsed;
            if (cachedPrice !== basePrice) return parsed;
            return base;
        } catch (_) {
            return base;
        }
    }, [passengerInfo?.basketData, carbinGradeInfo, userSelectCabinInfo?.resultno, idx, cruise?.codeToCruiseId, cruise?.id, cruise?.code]);

    const basketResult = basketData?.results?.[0];
    const basketCriteria = basketData?.meta?.criteria;
    const cruiseDetail = basketResult?.basketitems?.[0]?.cruisedetail;
    const perPerson = cruiseDetail?.perperson;

    const lineId = useMemo(() => (
        cruiseDetail?.line_id ||
        cruiseDetail?.lineid ||
        detailData?.schedule?.line_id ||
        detailData?.schedule?.lineid ||
        null
    ), [cruiseDetail?.line_id, cruiseDetail?.lineid, detailData?.schedule?.line_id, detailData?.schedule?.lineid]);

    const gratuityType = useMemo(() => {
        const normalizedLineId = Number(lineId);
        if (normalizedLineId === 16) return 'Hotel Service Charges';
        if ([17, 22, 73].includes(normalizedLineId)) return 'Gratuities';
        return '';
    }, [lineId]);

    const cautionItems = useMemo(() => resolveCautionByLineId(lineId), [lineId]);
    const termsContext = useMemo(() => ({
        lineId,
        shipId: detailData?.schedule?.ship_id || cruiseDetail?.shipid || cruiseDetail?.ship?.id,
        sailDate: detailData?.schedule?.saildate || cruiseDetail?.saildate || cruiseDetail?.startdate,
        departurePort: detailData?.schedule?.departure_port_name || cruiseDetail?.departureport || cruiseDetail?.startport,
        arrivalPort: detailData?.schedule?.arrival_port_name || cruiseDetail?.arrivalport || cruiseDetail?.endport,
    }), [
        lineId,
        detailData?.schedule?.ship_id,
        detailData?.schedule?.saildate,
        detailData?.schedule?.departure_port_name,
        detailData?.schedule?.arrival_port_name,
        cruiseDetail?.shipid,
        cruiseDetail?.ship?.id,
        cruiseDetail?.saildate,
        cruiseDetail?.startdate,
        cruiseDetail?.departureport,
        cruiseDetail?.startport,
        cruiseDetail?.arrivalport,
        cruiseDetail?.endport,
    ]);
    const termsSections = useMemo(() => resolveTermsByLineId(lineId, termsContext), [lineId, termsContext]);
    const specialTerms = termsSections?.[0] || DEFAULT_TERMS[0];
    const cancelTerms = termsSections?.[1] || DEFAULT_TERMS[1];
    const depositBookingFlag = Number(
        passengerInfo?.formDataInfo?.depositbooking ??
        passengerInfo?.formData?.depositbooking ??
        (isPartialPaymentLine(lineId) ? 1 : 0)
    );
    const reservationCreateResult = passengerInfo?.bookingResult || {};
    const paymentReservationKey = String(reservationCreateResult?.reservation_idx ?? bookingPayload?.reservation_idx ?? '');
    const paymentRetryBlocked = blockedPayments.has(paymentReservationKey);
    const reservationCreatePaymentSummary = reservationCreateResult?.payment_summary || null;
    const basketDepositMeta = useMemo(() => (
        depositBookingFlag === 1 ? {
            duedate: basketResult?.duedate || basketResult?.dueDate || basketResult?.due_date || null,
            totaldeposit: basketResult?.totaldeposit ?? basketResult?.totalDeposit ?? basketResult?.total_deposit ?? null,
        } : null
    ), [basketResult, depositBookingFlag]);

    const gratuityExtra = useMemo(() => {
        if (!gratuityType) return null;
        const extras = Array.isArray(basketResult?.basketitems?.[0]?.optionalextras)
            ? basketResult.basketitems[0].optionalextras
            : [];
        return findTraveltekGratuityExtra(extras, lineId);
    }, [basketResult?.basketitems, gratuityType, lineId]);

    const selectedGratuity = useMemo(() => {
        if (!gratuityExtra) return null;
        return findSelectedTraveltekGratuity(
            gratuityExtra,
            basketResult?.basketitems?.[0]?.selectedextras,
            lineId
        );
    }, [basketResult?.basketitems, gratuityExtra, lineId]);

    const isGratuitySelected = useMemo(() => {
        return isTraveltekGratuitySelected(
            gratuityExtra,
            basketResult?.basketitems?.[0]?.selectedextras,
            lineId
        );
    }, [basketResult?.basketitems, gratuityExtra, lineId]);

    const normalizeCautionItem = (item) =>
        typeof item === 'string' ? { text: item } : (item || { text: '' });

    const renderCautionSubItems = (items = [], depth = 1) => {
        if (!Array.isArray(items) || items.length === 0) return null;
        const listClassName = `caution-sublist caution-sublist-depth-${depth}`;

        return (
            <ul className={listClassName}>
                {items.map((rawItem, index) => {
                    const normalized = normalizeCautionItem(rawItem);
                    const subItems = Array.isArray(normalized.subItems) ? normalized.subItems : [];
                    const itemClassName = `caution-subitem caution-subitem-depth-${depth}${normalized.type === 'note' ? ' caution-subitem-note' : ''}`;

                    if (normalized.type === 'note') {
                        return (
                            <li key={`${depth}-${index}`} className={itemClassName}>
                                <div className='caution-subnote'>{normalized.text}</div>
                            </li>
                        );
                    }

                    return (
                        <li key={`${depth}-${index}`} className={itemClassName}>
                            <div className='caution-subline'>
                                <span className='caution-subdash'>-</span>
                                <span>{normalized.text}</span>
                            </div>
                            {renderCautionSubItems(subItems, depth + 1)}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderCautionItems = (items = []) => {
        if (!Array.isArray(items) || items.length === 0) {
            return <ul className='caution-contents' />;
        }

        return (
            <ul className='caution-contents'>
                {items.map((item, index) => {
                    const normalized = normalizeCautionItem(item);
                    const subItems = Array.isArray(normalized.subItems) ? normalized.subItems : [];

                    return (
                        <li key={`caution-${index}`} className='caution-item'>
                            <div className='dot'>
                                <DotIcon />
                            </div>
                            <div className='caution-text'>
                                <div>{normalized.text}</div>
                                {renderCautionSubItems(subItems, 1)}
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderTermItems = (items = [], listClassName = '') => (
        <ul className={listClassName}>
            {items.map((item, index) => {
                const isObject = typeof item === 'object' && item !== null;
                const text = isObject ? item.text : item;
                const type = isObject ? (item.type || 'item') : 'item';
                const itemClassName =
                    type === 'label'
                        ? 'term-item-label'
                        : type === 'note'
                            ? 'term-item-note'
                            : 'term-item';

                return (
                    <li key={`${type}-${index}`} className={itemClassName}>
                        {type === 'item' && (
                            <div className='dot'>
                                <DotIcon />
                            </div>
                        )}
                        <span style={item?.emphasis ? { color: '#d32f2f' } : undefined}>{text}</span>
                    </li>
                );
            })}
        </ul>
    );

    const { passengers: passengersList, warning: passengerMappingWarning } = useMemo(() => resolveTraveltekPassengers({
        counts: rawCounts,
        criteria: basketCriteria,
        cruiseDetail,
        childAges,
        paxFareInfo: passengerInfo?.paxFareInfo,
    }), [rawCounts, basketCriteria, cruiseDetail, childAges, passengerInfo?.paxFareInfo]);

    const passengerPriceDetails = useMemo(() => {
        const map = {};
        const paxFareInfo = passengerInfo?.paxFareInfo;
        if (!Array.isArray(perPerson)) {
            if (Array.isArray(paxFareInfo)) {
                paxFareInfo.forEach(pax => {
                    const guestNo = Number(pax?.pax_idx);
                    if (!Number.isFinite(guestNo)) return;
                    const fareList = Array.isArray(pax?.fare_list) ? pax.fare_list : [];
                    fareList.forEach(fare => {
                        const priceVal = Number(fare?.fare_amount) || 0;
                        const desc = fare?.fare_name || '';
                        if (!desc && !priceVal) return;
                        if (!map[guestNo]) map[guestNo] = [];
                        map[guestNo].push({
                            category: desc,
                            description: desc,
                            price: priceVal,
                        });
                    });
                });
            }
            return map;
        }

        const isOnboardCreditCategory = (category = {}) => {
            const normalized = `${category?.category || ''} ${category?.description || ''}`
                .toLowerCase()
                .replace(/[^a-z]/g, '');
            return normalized.includes('onboardcredit');
        };

        const isGratuityCategory = (category = {}) => {
            return isTraveltekGratuityPriceItem({
                category: category?.category || '',
                description: category?.description || '',
                lineId,
            });
        };

        perPerson.forEach(category => {
            if (!category) return;
            if (isOnboardCreditCategory(category)) return;

            const isGrats = isGratuityCategory(category);
            const prices = Array.isArray(category.prices) ? category.prices : [];
            // 선결제 미선택: 선상팁은 완전히 제외
            if (!isGratuitySelected && isGrats) return;

            const categoryLabel = category?.category || category?.description || '';
            prices.forEach(priceInfo => {
                const guestNo = Number(priceInfo?.guestno);
                if (!Number.isFinite(guestNo)) return;
                if (!map[guestNo]) map[guestNo] = [];
                const normalizedPriceItem = normalizeTraveltekPassengerPriceItem({
                    category: categoryLabel,
                    description: category?.description || categoryLabel || '',
                    lineId,
                });
                const targetDesc = normalizedPriceItem.description;
                const priceVal = Number(priceInfo?.price) || 0;

                const existing = map[guestNo].find(item => (item?.description || '').toLowerCase() === targetDesc.toLowerCase());
                if (existing) {
                    existing.price = (Number(existing.price) || 0) + priceVal;
                } else {
                    map[guestNo].push({
                        category: normalizedPriceItem.category,
                        description: targetDesc,
                        price: priceVal,
                    });
                }
            });
        });
        const hasGratuityInPerperson = perPerson.some(category => {
            return isTraveltekGratuityPriceItem({
                category: category?.category || '',
                description: category?.description || '',
                lineId,
            });
        });
        if (!hasGratuityInPerperson && isGratuitySelected) {
            const gratuityLabel = selectedGratuity?.description || gratuityExtra?.description || gratuityType || 'Gratuities';
            const gratuityCategory = gratuityType || gratuityLabel;
            const gratuityPriceSource = selectedGratuity?.price ?? gratuityExtra?.price;
            const gratuityQty = selectedGratuity?.quantity ?? gratuityExtra?.quantity ?? 1;
            const gratuityTotal = Number(gratuityPriceSource) * (Number(gratuityQty) || 1);
            if (Number.isFinite(gratuityTotal) && gratuityTotal > 0 && passengersList.length > 0) {
                const perGuest = Math.round((gratuityTotal / passengersList.length) * 100) / 100;
                passengersList.forEach(pax => {
                    const guestNo = pax.guestNo;
                    if (!map[guestNo]) map[guestNo] = [];
                    const hasSame = map[guestNo].some(item => (item?.description || '').toLowerCase() === gratuityLabel.toLowerCase());
                    if (!hasSame) {
                        map[guestNo].push({
                            category: gratuityCategory,
                            description: gratuityLabel,
                            price: perGuest,
                        });
                    }
                });
            }
        }
        return map;
    }, [gratuityExtra, gratuityType, isGratuitySelected, lineId, passengerInfo?.paxFareInfo, passengersList, perPerson, selectedGratuity]);

    const passengerBenefitDetails = useMemo(() => {
        const map = {};
        if (!Array.isArray(perPerson)) return map;

        perPerson.forEach(category => {
            if (!category) return;
            const categoryKey = (category?.category || '').toLowerCase();
            if (categoryKey !== 'onboardcredit') return;

            const prices = Array.isArray(category?.prices) ? category.prices : [];
            const categoryLabel = category?.category || category?.description || '';
            const rawDesc = category?.description || categoryLabel || 'On-Board Credit';

            prices.forEach(priceInfo => {
                const guestNo = Number(priceInfo?.guestno);
                if (!Number.isFinite(guestNo)) return;
                if (!map[guestNo]) map[guestNo] = [];

                const priceVal = Number(priceInfo?.price) || 0;
                const existing = map[guestNo].find(item => (item?.description || '').toLowerCase() === rawDesc.toLowerCase());
                if (existing) {
                    existing.price = (Number(existing.price) || 0) + priceVal;
                } else {
                    map[guestNo].push({
                        category: categoryLabel,
                        description: rawDesc,
                        price: priceVal,
                    });
                }
            });
        });

        return map;
    }, [perPerson]);

    // 모바일 용 버튼 렌더링 관련 -----
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 651);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Portal Container 생성 및 관리
    useEffect(() => {
        // 모바일 뷰에서 하단 고정 버튼을 포털로 렌더링하기 위해 footer 이후에 컨테이너를 생성한다.
        const footerEl = document.querySelector('.footer');
        if (!footerEl) {
            console.warn("Footer element not found. Portal container cannot be created.");
            return;
        }
        const container = document.createElement('div');
        footerEl.parentNode.insertBefore(container, footerEl.nextSibling);
        setPortalContainer(container);
        return () => {
            if (footerEl.parentNode && container.parentNode === footerEl.parentNode) {
                footerEl.parentNode.removeChild(container);
            }
        };
    }, []);

    // 포탈 컨테이너에 렌더된 엘리먼트 높이를 측정해서 padding-top 적용
    useLayoutEffect(() => {
        // 포털에 렌더링된 요소 높이만큼 여백을 주어 본문이 가려지지 않게 조정한다.
        if (!portalContainer) return;
        const wrapper = portalContainer.firstElementChild;
        if (!wrapper || !isMobile) {
            portalContainer.style.paddingTop = '0px';
            return;
        }
        // The required agreement can wrap at narrower widths; reserve its actual
        // height as well as the payment button so the fixed bar covers no content.
        const updateHeight = () => {
            portalContainer.style.paddingTop = `${wrapper.getBoundingClientRect().height}px`;
        };
        updateHeight();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
        observer?.observe(wrapper);
        window.addEventListener('resize', updateHeight);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, [portalContainer, isMobile]);
    // 모바일 용 버튼 렌더링 관련 끝 -----

    // 옵션/결제 금액 선택 비활성화
    // const [selectedOption, setSelectedOption] = useState('option-0');
    // const [selectedPayment, setSelectedPayment] = useState('deposit');

    // 결제 정보 입력 필드 상태
    const [cardInfo, setCardInfo] = useState({
        lastName: '',
        firstName: '',
        cardNumber1: '',
        cardNumber2: '',
        cardNumber3: '',
        cardNumber4: '',
        expiryMonth: '',
        expiryYear: '',
        csc: '',
    });

    // 입력 필드 ref (입력완료시 다음 으로 focus)
    const cardNum2Ref = useRef(null);
    const cardNum3Ref = useRef(null);
    const cardNum4Ref = useRef(null);
    const expiryMonthRef = useRef(null);
    const expiryYearRef = useRef(null);
    const cscRef = useRef(null);

    const [errors, setErrors] = useState({});

    // 카드 번호로 카드 브랜드/코드 탐지
    const fullCardNumberForType = useMemo(() => (
        [
            cardInfo.cardNumber1,
            cardInfo.cardNumber2,
            cardInfo.cardNumber3,
            cardInfo.cardNumber4,
        ].join('')
    ), [cardInfo.cardNumber1, cardInfo.cardNumber2, cardInfo.cardNumber3, cardInfo.cardNumber4]);

    const detectedCard = useMemo(() => {
        try {
            const digits = String(fullCardNumberForType || '').replace(/\D/g, '');
            if (!digits) return null;
            const results = creditCardType(digits);
            return Array.isArray(results) && results.length > 0 ? results[0] : null;
        } catch (_) {
            return null;
        }
    }, [fullCardNumberForType]);

    const detectedBrand = detectedCard?.type || '';
    const mappedCardType = useMemo(() => cardTypeMapping(detectedBrand), [detectedBrand]);

    // 허용 카드 타입 (Visa, MasterCard, Amex 만 허용)
    const ALLOWED_CARD_TYPES = ['VIS', 'MSC', 'AMX'];

    // Luhn 체크 함수 (카드번호 유효성)
    const luhnCheck = (numStr) => {
        const s = String(numStr || '').replace(/\D/g, '');
        if (!s) return false;
        let sum = 0;
        let shouldDouble = false;
        for (let i = s.length - 1; i >= 0; i--) {
            let digit = s.charCodeAt(i) - 48; // '0' = 48
            if (shouldDouble) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
            shouldDouble = !shouldDouble;
        }
        return sum % 10 === 0;
    };

    // 카드 브랜드/번호 유효성 검사 (완성 길이에 도달하면 검사)
    useEffect(() => {
        const digits = String(fullCardNumberForType || '').replace(/\D/g, '');
        // 우선 credit-card-type이 알려주는 해당 브랜드의 허용 길이를 사용, 없으면 대표 길이 집합 사용
        const brandLengths = Array.isArray(detectedCard?.lengths) ? detectedCard.lengths : null;
        const fallbackLengths = new Set([13, 15, 16, 19]);
        const isCompleteLength = brandLengths ? brandLengths.includes(digits.length) : fallbackLengths.has(digits.length);

        let cardTypeError = '';
        let cardNumberError = '';

        if (isCompleteLength) {
            // 1) 허용 카드 타입 검사 (우선순위: 브랜드 에러가 있으면 번호 에러는 노출하지 않음)
            if (!mappedCardType || !ALLOWED_CARD_TYPES.includes(mappedCardType)) {
                cardTypeError = '지원하지 않는 카드입니다. Visa/Master/Amex만 사용 가능합니다.';
            } else if (!luhnCheck(digits)) {
                // 2) Luhn 검사 (허용 브랜드일 때만 수행/노출)
                cardNumberError = '유효하지 않은 카드번호입니다. 다시 확인해 주세요.';
            }
        } else {
            // 길이가 미완성인 경우 번호 오류는 비움 (입력 중)
            cardTypeError = '';
            cardNumberError = '';
        }

        setErrors(prev => ({ ...prev, cardType: cardTypeError, cardNumberFull: cardNumberError }));
    }, [detectedCard, mappedCardType, fullCardNumberForType]);

    // 각 입력 필드별 유효성 검사 로직을 한 곳에 모아 재사용한다.
    const validateField = (name, value) => {
        switch (name) {
            case 'lastName':
                return value ? '' : '성을 입력해주세요.';
            case 'firstName':
                return value ? '' : '이름을 입력해주세요.';
            case 'cardNumber1':
            case 'cardNumber2':
            case 'cardNumber3':
            case 'cardNumber4': {
                if (!value) return '카드번호를 입력해주세요.';
                const onlyDigits = /^\d+$/;
                if (!onlyDigits.test(value)) return '숫자만 입력해주세요.';
                const requiredLen = (name === 'cardNumber4' && mappedCardType === 'AMX') ? 3 : 4;
                if (value.length !== requiredLen) {
                    return requiredLen === 3 ? '3자리 숫자를 입력해주세요.' : '4자리 숫자를 입력해주세요.';
                }
                return '';
            }
            case 'expiryMonth':
                if (!value) return '유효기간 월을 입력해주세요.';
                if (!/^\d{2}$/.test(value)) return '2자리 숫자를 입력해주세요.';
                {
                    const mm = Number(value);
                    if (mm < 1 || mm > 12) return '1~12 사이의 숫자를 입력해주세요.';
                }
                return '';
            case 'expiryYear':
                if (!value) return '유효기간 년을 입력해주세요.';
                if (!/^\d{2}$/.test(value)) return '2자리 숫자를 입력해주세요.';
                return '';
            case 'csc': {
                if (!value) return '보안코드를 입력해주세요.';
                const onlyDigits = /^\d+$/;
                if (!onlyDigits.test(value)) return '숫자만 입력해주세요.';
                if (mappedCardType === 'AMX') {
                    // AMEX: CID 4자리 고정
                    if (value.length !== 4) return 'CID 4자리를 입력해주세요.';
                } else if (mappedCardType === 'VIS' || mappedCardType === 'MSC') {
                    if (value.length !== 3) return mappedCardType === 'VIS' ? 'CVV 3자리를 입력해주세요.' : 'CVC 3자리를 입력해주세요.';
                } else {
                    if (value.length < 3 || value.length > 4) return '3자리 또는 4자리 숫자를 입력해주세요.';
                }
                return '';
            }
            default:
                return '';
        }
    };

    // blur 시점에 개별 필드 오류 메시지를 즉시 업데이트한다.
    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    // 일반 텍스트 입력 필드 핸들러 (성, 이름)
    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === 'lastName' || name === 'firstName') {
            processedValue = value.replace(/[^a-zA-Z]/g, '').toUpperCase();
        }

        setCardInfo(prev => ({ ...prev, [name]: processedValue }));
        if (errors[name]) {
            const error = validateField(name, processedValue);
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    // 카드 번호 입력을 4개의 인풋으로 나눠 처리하며 자동 포커스 이동을 관리한다.
    const handleCardNumberChange = (e, nextFieldRef) => {
        const { name, value } = e.target;
        const formattedValue = value.replace(/\D/g, '');

        setCardInfo(prev => ({ ...prev, [name]: formattedValue }));

        // 현재 필드 유효성 검사
        const errSelf = validateField(name, formattedValue);
        setErrors(prev => ({ ...prev, [name]: errSelf }));

        // 다음 필드로 넘길 조건:
        // - 현재 필드가 요구 길이를 채우고(AMEX의 경우 cardNumber4는 3자리) 오류가 없을 때만
        const requiredLen = (name === 'cardNumber4' && mappedCardType === 'AMX') ? 3 : 4;
        if (formattedValue.length === requiredLen && errSelf === '' && nextFieldRef && nextFieldRef.current) {
            // cardNumber4에서 만약 expiryMonth로 넘기려면, 4개 모두 유효 + 전체 번호가 유효해야 함
            if (name === 'cardNumber4') {
                const fields = ['cardNumber1','cardNumber2','cardNumber3'];
                const allOk = fields.every(fn => {
                    const val = (fn === name ? formattedValue : (cardInfo[fn] || ''));
                    return validateField(fn, val) === '' && val.replace(/\D/g,'').length === 4;
                });
                if (!allOk) return; // 이전 카드번호 블록이 유효하지 않으면 포커스 이동 금지

                // 전체 카드번호 유효성 (브랜드 길이 완성 + 허용 브랜드 + Luhn 통과)
                const cn = { ...cardInfo, [name]: formattedValue };
                const allDigits = [cn.cardNumber1, cn.cardNumber2, cn.cardNumber3, cn.cardNumber4].join('').replace(/\D/g, '');
                const guess = creditCardType(allDigits);
                const detected = Array.isArray(guess) && guess.length ? guess[0] : null;
                const brandLengths = Array.isArray(detected?.lengths) ? detected.lengths : null;
                const fallbackLengths = new Set([13, 15, 16, 19]);
                const isCompleteLength = brandLengths ? brandLengths.includes(allDigits.length) : fallbackLengths.has(allDigits.length);
                const mapped = cardTypeMapping(detected?.type || '');
                const brandOk = !!mapped && ['VIS','MSC','AMX'].includes(mapped);
                const luhnOk = luhnCheck(allDigits);
                if (!(isCompleteLength && brandOk && luhnOk)) return;
            }
            nextFieldRef.current.focus();
        }
    };

    // 월/년 입력 시 숫자만 허용하고 자리수 충족 시 다음 필드로 이동한다.
    const handleExpiryDateChange = (e, nextFieldRef) => {
        const { name, value } = e.target;
        const formattedValue = value.replace(/\D/g, '');

        setCardInfo(prev => ({ ...prev, [name]: formattedValue }));

        // 현재 필드 유효성 검사
        const errSelf = validateField(name, formattedValue);
        setErrors(prev => ({ ...prev, [name]: errSelf }));

        // 2자리 + 에러 없음일 때만 다음 필드로 포커스 이동
        if (formattedValue.length === 2 && errSelf === '' && nextFieldRef && nextFieldRef.current) {
            nextFieldRef.current.focus();
        }
    };

    // 보안코드는 카드 타입별 자리수 규칙이 다르므로 동적으로 잘라낸다.
    const handleCscChange = (e) => {
        const { name, value } = e.target;
        const maxLen = mappedCardType === 'AMX' ? 4 : (mappedCardType === 'VIS' || mappedCardType === 'MSC' ? 3 : 4);
        const formattedValue = value.replace(/\D/g, '').substring(0, maxLen);
        setCardInfo(prev => ({ ...prev, [name]: formattedValue }));
        // 즉시 유효성 검사(자리수 부족일 때도 에러 표시)
        const err = validateField(name, formattedValue);
        setErrors(prev => ({ ...prev, [name]: err }));
    };

    // YY -> YYYY 변환 (예: '27' -> 2027)
    const toFourDigitYear = (val) => {
        const s = String(val || '').replace(/\D/g, '');
        if (s.length === 4) return Number(s); //  4자리면 그대로 사용으로 변경시
        if (s.length === 2) return 2000 + Number(s);
        return 0;
    };

    // 기본 요금 계산 (옵션 가격 제외)
    const calculatedBasePrice = useMemo(() => {
        // 기존 방식 (pricePerAge 기반) - 비활성화 대상 키가 없을 수 있어 가드
        // if (ages && pricePerAge) {
        //   return Object.entries(ages)
        //     .flatMap(([ageType, ageList]) =>
        //       ageList.map(() => {
        //         const originalFare = pricePerAge[ageType] || 0;
        //         const discountAmt = Math.round(originalFare * ((disCountPricePercent || 0) / 100));
        //         const afterDiscount = originalFare - discountAmt;
        //         const taxAmt = Math.round(afterDiscount * ((taxPrice || 0) / 100));
        //         return afterDiscount + taxAmt;
        //       })
        //     )
        //     .reduce((sum, val) => sum + val, 0);
        // }

        // 새 페이로드 구조: gridPrice.price 사용 (총액)
        const gridTotal = Number(carbinGradeInfo?.gridPrice?.price) || 0;
        return gridTotal;
    }, [carbinGradeInfo]);

    // --- 옵션 데이터 버전 1: 옵션 옆에 추가 가격을 표시하는 버전 (비활성화)
    // const options = bookingOptionData.map(option => ({
    //     ...option,
    //     price: `+ $${Number(option.addPrice).toLocaleString()}` // addPrice를 price로 변환하여 UI에 표시
    // }));
    

    // --- 옵션 데이터 버전 2: 옵션 옆에 합산된 가격을 표시하는 버전 ---
    // const options = bookingOptionData.map(option => ({
    //     ...option,
    //     price: `$${(calculatedBasePrice + Number(option.addPrice)).toLocaleString()}` // 기본 요금 + 옵션 추가 가격
    // }));

    const ageLabelMap = passengerLabels;

    // 실제 결제 시 표시할 총 요금은 create_reservation 응답의 payment_summary를 우선 사용한다.
    const totalPriceCalculated = useMemo(() => {
        const summaryTotal = parseMoneyValue(reservationCreatePaymentSummary?.total_amount);
        if (summaryTotal !== null) {
            return summaryTotal;
        }
        return Number(basketData?.results?.[0]?.totalprice) || 0;
    }, [basketData, reservationCreatePaymentSummary?.total_amount]);
    const depositAmountCalculated = useMemo(() => (
        depositBookingFlag === 1
            ? (
                parseMoneyValue(reservationCreatePaymentSummary?.deposit_amount)
                ?? Number(basketData?.results?.[0]?.totaldeposit ?? basketData?.results?.[0]?.totalDeposit ?? basketData?.results?.[0]?.total_deposit ?? 0)
            )
            : 0
    ), [basketData, depositBookingFlag, reservationCreatePaymentSummary?.deposit_amount]);
    const finalAmountCalculated = useMemo(() => (
        parseMoneyValue(reservationCreatePaymentSummary?.final_amount)
            ?? Math.max(totalPriceCalculated - depositAmountCalculated, 0)
    ), [depositAmountCalculated, reservationCreatePaymentSummary?.final_amount, totalPriceCalculated]);
    const finalDueDateCalculated = useMemo(() => (
        reservationCreatePaymentSummary?.final_due_date
            || basketData?.results?.[0]?.duedate
            || basketData?.results?.[0]?.dueDate
            || basketData?.results?.[0]?.due_date
            || null
    ), [basketData, reservationCreatePaymentSummary?.final_due_date]);
    const paymentAmountCalculated = useMemo(() => (
        depositBookingFlag === 1 && depositAmountCalculated > 0
            ? depositAmountCalculated
            : totalPriceCalculated
    ), [depositBookingFlag, depositAmountCalculated, totalPriceCalculated]);
    const showPaymentBreakdown = depositBookingFlag === 1 && depositAmountCalculated > 0;
    const paymentSummaryTypeLabel = showPaymentBreakdown ? '예약금 결제' : '총 요금 결제';
    const paymentSummaryNote = useMemo(() => {
        if (!showPaymentBreakdown) {
            return '이번 단계에서 총 요금이 결제됩니다.';
        }
        if (finalAmountCalculated <= 0) {
            return '이번 단계에서 결제 후 남은 잔금은 없습니다.';
        }

        const noteParts = [`잔금 ${formatUsdCurrency(finalAmountCalculated)}`];
        const dueDateLabel = formatDueDateStatus(finalDueDateCalculated);
        if (dueDateLabel !== '-') {
            noteParts.push(dueDateLabel);
        }
        return noteParts.join(' · ');
    }, [showPaymentBreakdown, finalAmountCalculated, finalDueDateCalculated]);

    // 결제 금액 선택(예약금/전액) 데이터 비활성화
    // const payMent = useMemo(() => {
    //     const depositAmount = Math.round(totalPriceCalculated * 0.1); // 전체 가격의 10%
    //     const remainingBalance = totalPriceCalculated - depositAmount;
    //     return [
    //         { id: 'deposit', label:'예약금 결제', payPrice: `$${depositAmount.toLocaleString()}`, balance: `$${remainingBalance.toLocaleString()}`, dueDate:'2025-09-17 17시까지' },
    //         { id: 'total', label:'전체 금액 결제', payPrice: `$${totalPriceCalculated.toLocaleString()}`, balance: '$0.00', dueDate:'' },
    //     ];
    // }, [totalPriceCalculated]);

    const [isFormValid, setIsFormValid] = useState(false);
    const cancellationAgreementScope = JSON.stringify({
        reservationIdx: reservationCreateResult?.reservation_idx ?? bookingPayload?.reservation_idx ?? null,
        scheduleId: idx ?? null,
        cruiseId: cruise?.codeToCruiseId ?? cruise?.id ?? cruise?.code ?? null,
        cabin: userSelectCabinInfo?.cabinno ?? null,
        context: termsContext,
        terms: cancelTerms,
    });
    const { agreed: cancellationTermsAgreed, setAgreed: setCancellationTermsAgreed } =
        useCancellationAgreement(cancellationAgreementScope, !!bookingPayload);

    useEffect(() => {
        // 모든 카드 입력 필드가 채워지고 오류가 없을 때만 결제 버튼을 활성화한다.
        const isCardInfoValid = Object.values(cardInfo).every(value => value !== '') && Object.values(errors).every(error => error === '');
        // 옵션/결제 금액 선택 비활성화에 따라 카드 정보만 검증
        setIsFormValid(isCardInfoValid);
    }, [cardInfo, errors]);

    if (!bookingPayload) return null;

    // 결제 버튼 클릭 시 Traveltek API에 전달할 페이로드를 구성하고 요청을 호출한다.
    const handlePaymentClick = async () => {
        if (isSubmitting || paymentInFlightRef.current || paymentRetryBlocked) return;
        if (!cancellationTermsAgreed) {
            alert('취소료 규정에 동의한 후 결제를 진행해주세요.');
            return;
        }

        const fullCardNumber = [
            cardInfo.cardNumber1,
            cardInfo.cardNumber2,
            cardInfo.cardNumber3,
            cardInfo.cardNumber4,
        ].join('');

        if (!ALLOWED_CARD_TYPES.includes(mappedCardType)) {
            alert('지원하지 않는 카드입니다. Visa/Master/Amex만 사용 가능합니다.');
            return;
        }
        const digitsForLuhn = String(fullCardNumber).replace(/\D/g, '');
        if (!digitsForLuhn || !luhnCheck(digitsForLuhn)) {
            alert('유효하지 않은 카드번호입니다. 다시 확인해 주세요.');
            return;
        }

        const contactInfo = passengerInfo?.formDataInfo?.contact || {};
        const rawAddress1 = contactInfo.address1;
        let normalizedAddress1 = contactInfo.address1 || '';
        let normalizedAddress2 = contactInfo.address2 || '';
        if (typeof rawAddress1 === 'string') {
            const parts = rawAddress1.split(',').map((part) => part.trim()).filter(Boolean);
            if (parts.length) {
                normalizedAddress1 = parts.slice(0, Math.min(parts.length, 2)).join(', ');
                if (parts.length > 2) {
                    normalizedAddress2 = parts.slice(2).join(', ');
                }
            }
        }

        const formDataSource = passengerInfo?.formDataInfo || passengerInfo?.formData;
        if (!formDataSource) {
            alert('예약 정보가 없습니다. 다시 시도해 주세요.');
            return;
        }

        const normalizedLineId = Number(lineId);
        if (!Number.isFinite(normalizedLineId) || normalizedLineId <= 0) {
            alert('선사 정보가 올바르지 않습니다. 다시 시도해 주세요.');
            return;
        }

        const cscValue = String(cardInfo.csc || '').replace(/\D/g, '').trim();
        if (!cscValue) {
            alert('보안코드를 입력해주세요.');
            return;
        }

        const ccardPayload = {
            "amount": paymentAmountCalculated,
            "nameoncard": `${String(cardInfo.lastName ?? '').trim()} ${String(cardInfo.firstName ?? '').trim()}`.trim(),
            "cardtype": mappedCardType || "",
            "cardnumber": fullCardNumber,
            "expirymonth": String(cardInfo.expiryMonth || "").padStart(2, "0"),
            "expiryyear": Number(toFourDigitYear(cardInfo.expiryYear)),
            "signature": cscValue,
            "title": passengerInfo?.formDataInfo?.contact?.title || '',
            "firstname": contactInfo.firstname || '',
            "lastname": contactInfo.lastname || '',
            "postcode": contactInfo.postcode || '',
            "address1": normalizedAddress1,
            "address2": normalizedAddress2,
            "homecity": contactInfo.city || '',
            "county": contactInfo.county || '',
            "country": contactInfo.country || ''
        };

        paymentInFlightRef.current = true;
        setIsSubmitting(true);
        let paymentExecuted = false;
        try {
            const reservationIdx = Number.parseInt(
                String(
                    reservationCreateResult?.reservation_idx ??
                    bookingPayload?.reservation_idx ??
                    ''
                ).trim(),
                10
            );
            if (!Number.isFinite(reservationIdx) || reservationIdx <= 0) {
                alert('결제할 예약 정보가 없습니다. 다시 예약을 진행해 주세요.');
                return;
            }

            const paymentResponse = await apiPost('/reservation/pay_reservation.php', {
                reservation_idx: reservationIdx,
                line_id: normalizedLineId,
                cancellation_terms_agreed: true,
                ccard: ccardPayload,
            });
            if (paymentResponse?.success !== true) {
                const error = new Error(paymentResponse?.message || '결제 요청에 실패했습니다.');
                error.response = { status: 200, data: paymentResponse };
                throw error;
            }
            paymentExecuted = true;

            const detailResponse = await apiGet('/reservation/get_reservation_detail.php', {
                idx: reservationIdx,
            });

            if (detailResponse?.success !== true) {
                throw new Error(detailResponse?.message || '결제 후 예약 정보 동기화에 실패했습니다.');
            }

            const normalizedStatus = detailResponse?.reservation_status;
            const passengerPayload = {
                ...passengerInfo,
                formDataInfo: formDataSource,
                bookingResult: reservationCreateResult,
                basketData,
                ...(basketDepositMeta ? { depositMeta: basketDepositMeta } : {}),
                reservation_create_date:
                    passengerInfo?.reservation_create_date ||
                    reservationCreateResult?.reservation_create_date ||
                    null,
            };
            const payloadForNextStep = {
                ...detailResponse,
                status:
                    normalizedStatus !== null && normalizedStatus !== undefined
                        ? String(normalizedStatus)
                        : (detailResponse?.status ?? ''),
                payment_execution: paymentResponse,
                reservationType: true,
                passengerInfo: passengerPayload,
            };
            onNextStep(payloadForNextStep, 1);
        } catch (error) {
            console.error('Error sending payment request:', error);
            const requiresReview = shouldBlockPaymentRetry(error, paymentExecuted);
            if (requiresReview) setBlockedPayments(previous => new Set([...previous, paymentReservationKey]));
            const responseData = error?.response?.data;
            const upstreamErrors = Array.isArray(responseData?.errors)
                ? responseData.errors
                : Array.isArray(responseData?.payment_response?.errors)
                    ? responseData.payment_response.errors
                    : Array.isArray(responseData?.traveltek_response?.errors)
                        ? responseData.traveltek_response.errors
                    : [];
            const firstUpstreamError = upstreamErrors[0];
            const upstreamMessage =
                (typeof firstUpstreamError === 'string' ? firstUpstreamError : null) ||
                firstUpstreamError?.message ||
                firstUpstreamError?.msg ||
                firstUpstreamError?.description ||
                '';
            const explicitMessage =
                responseData?.message ||
                responseData?.msg ||
                error?.message ||
                '';
            const fallbackMessage = paymentExecuted
                ? '결제는 완료되었을 수 있으나 예약 정보 동기화에 실패했습니다. 마이페이지에서 상태를 다시 확인해주세요.'
                : '결제 요청에 실패했습니다. 잠시 후 다시 시도해주세요.';
            const errorMessage = requiresReview ? PAYMENT_REVIEW_MESSAGE : (explicitMessage || upstreamMessage || fallbackMessage);

            alert({
                title: requiresReview ? '결제 상태 확인이 필요합니다.' : '결제 실패',
                content: <p>{errorMessage}</p>,
                showConfirmButton: false,
                footerContent: <button type="button" className="btn">확인</button>,
            });
        } finally {
            paymentInFlightRef.current = false;
            setIsSubmitting(false);
        }
    };

    // 모바일/데스크톱 공용으로 사용하는 결제 버튼 조각
    const paymentButtonContent = (
        <div className='depth_4-guest-payment-btn-wrapper'>
            <CancellationTermsAgreement
                checked={cancellationTermsAgreed}
                onChange={setCancellationTermsAgreed}
                disabled={isSubmitting}
                onViewTerms={() => {
                    cancellationTermsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    cancellationTermsRef.current?.focus({ preventScroll: true });
                }}
            />
            <PaymentButton
                onClick={handlePaymentClick}
                disabled={!isFormValid || !cancellationTermsAgreed || isSubmitting || paymentRetryBlocked}
                text={
                    isSubmitting
                        ? '결제 진행 중...'
                        : (isMobile
                            ? `${formatUsdCurrency(paymentAmountCalculated)} 결제하기`
                            : `결제하기`)
                }
            />
            {paymentRetryBlocked && <p role="alert">{PAYMENT_REVIEW_MESSAGE}</p>}
        </div>
    );

    return (
        <>
            {isSubmitting && <Loading isLoading={true} />}
            <div className='guest-payment-wrapper'>
                {/* 결제 정보 입력 컨텐츠 */}
                <div className='guest-payment-content'>
                    {/*
                    추가 옵션 (비활성화)
                    <div className='payment-content'>
                        <h2>추가 옵션 선택</h2>
                        <h3>선택 항목</h3>
                        <div className="payment-content-form">
                            {options.map((opt) => (
                                <label
                                    key={opt.id}
                                    htmlFor={opt.id}
                                    className={`payment-option ${selectedOption === opt.id ? 'selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        id={opt.id}
                                        name="optionSelect"
                                        checked={selectedOption === opt.id}
                                        onChange={() => setSelectedOption(opt.id)}
                                        style={{ display: 'none' }} // 라디오 숨기기
                                    />
                                    <div className='info-title'>
                                        {selectedOption === opt.id ? <CircleFilledIcon /> : <CircleOutlineIcon />}
                                        <p>{opt.label}<span>{opt.price}</span></p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    */}
                    {/*
                    결제 방법 (예약금/전액 금액 선택 비활성화)
                    <div className='payment-content'>
                        <h2>결제 방법 선택</h2>
                        <h3>결제 금액</h3>
                        <div className="payment-content-form">
                            {payMent.map((opt) => (
                                <label
                                    key={opt.id}
                                    htmlFor={opt.id}
                                    className={`payment-price-option ${selectedPayment === opt.id ? 'selected' : ''}`}
                                    style={{minHeight:'104px'}}
                                >
                                    <div className='price-info-wrapper'>
                                        <input
                                            type="radio"
                                            id={opt.id}
                                            name="paymentMethod"
                                            checked={selectedPayment === opt.id}
                                            onChange={() => setSelectedPayment(opt.id)}
                                            style={{ display: 'none' }} // 라디오 숨기기
                                        />
                                        <div className='info-title'>
                                            {selectedPayment === opt.id ? <CircleFilledIcon /> : <CircleOutlineIcon />}
                                            <p className='now-pay-price'>{opt.label}<span>{opt.payPrice}</span></p>
                                        </div>
                                        <div className='info-payment-content'>
                                            <p className='balance-pay-price'>결제 잔금<span>{opt.balance}</span></p>
                                            { opt.dueDate ? <p className='balance-pay-due-date'>결제 기한<span>{opt.dueDate}</span></p> : ''}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                    */}
                    {/* 결제 정보 */}
                    <div className='payment-content'>
                        <div className='payment-content-title'>
                            <h2>결제 정보</h2>
                            <div className='available-card-list'>
                                <VisaIcon />
                                <MasterIcon />
                                <AmexIcon />
                            </div>
                        </div>
                        
                        <div className="payment-content-form pay-info">
                            <div className="payment-charge-summary">
                                <span className="payment-charge-summary-label">이번 결제 금액</span>
                                <div className="payment-charge-summary-main">
                                    <strong className="payment-charge-summary-amount">
                                        {formatUsdCurrency(paymentAmountCalculated)}
                                    </strong>
                                    <span className="payment-charge-summary-type">
                                        {paymentSummaryTypeLabel}
                                    </span>
                                </div>
                                <p className="payment-charge-summary-note">{paymentSummaryNote}</p>
                            </div>
                            <div className="form-group">
                                <label htmlFor="lastName">성(Last Name)</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={cardInfo.lastName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="성을 입력해주세요 (예: Hong)"
                                    className={errors.lastName ? 'is-invalid' : ''}
                                />
                                {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="firstName">이름(First Name)</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={cardInfo.firstName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="이름을 입력해주세요(예: Gildong)"
                                    className={errors.firstName ? 'is-invalid' : ''}
                                />
                                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                            </div>
                            <div className="form-group">
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                    <label>카드 번호</label>
                                    <InfoButton placement="" offsetY={8}>
                                        <ul style={{fontSize:'14px', fontWeight:'400', lineHeight:'140%'}}>
                                            <li>
                                                AMEX 카드인 경우 자리수 상관없이 채워주시기 바랍니다.
                                            </li>
                                        </ul>
                                    </InfoButton>
                                </div>
                                <div className="card-number-group">
                                    <input
                                        type="text"
                                        id="cardNumber1"
                                        name="cardNumber1"
                                        value={cardInfo.cardNumber1}
                                        onChange={(e) => handleCardNumberChange(e, cardNum2Ref)}
                                        onBlur={handleBlur}
                                        maxLength="4"
                                        className={errors.cardNumber1 ? 'is-invalid' : ''}
                                    />
                                    <span>-</span>
                                    <input
                                        type="text"
                                        id="cardNumber2"
                                        name="cardNumber2"
                                        value={cardInfo.cardNumber2}
                                        onChange={(e) => handleCardNumberChange(e, cardNum3Ref)}
                                        onBlur={handleBlur}
                                        maxLength="4"
                                        ref={cardNum2Ref}
                                        className={errors.cardNumber2 ? 'is-invalid' : ''}
                                    />
                                    <span>-</span>
                                    <input
                                        type="text"
                                        id="cardNumber3"
                                        name="cardNumber3"
                                        value={cardInfo.cardNumber3}
                                        onChange={(e) => handleCardNumberChange(e, cardNum4Ref)}
                                        onBlur={handleBlur}
                                        maxLength="4"
                                        ref={cardNum3Ref}
                                        className={errors.cardNumber3 ? 'is-invalid' : ''}
                                    />
                                    <span>-</span>
                                    <input
                                        type="text"
                                        id="cardNumber4"
                                        name="cardNumber4"
                                        value={cardInfo.cardNumber4}
                                        onChange={(e) => handleCardNumberChange(e, expiryMonthRef)}
                                        onBlur={handleBlur}
                                        maxLength={mappedCardType === 'AMX' ? 3 : 4}
                                        ref={cardNum4Ref}
                                        className={errors.cardNumber4 ? 'is-invalid' : ''}
                                    />
                                </div>
                                {(errors.cardNumber1 || errors.cardNumber2 || errors.cardNumber3 || errors.cardNumber4) && (
                                    <div className="invalid-feedback" style={{display: 'block'}}>
                                        {errors.cardNumber1 || errors.cardNumber2 || errors.cardNumber3 || errors.cardNumber4}
                                    </div>
                                )}
                                {errors.cardType && (
                                    <div className="invalid-feedback" style={{display: 'block'}}>
                                        {errors.cardType}
                                    </div>
                                )}
                                {errors.cardNumberFull && (
                                    <div className="invalid-feedback" style={{display: 'block'}}>
                                        {errors.cardNumberFull}
                                    </div>
                                )}
                            </div>
                            <div className="form-group-inline">
                                <div className="form-group">
                                    <label htmlFor='expiryMonth'>유효 기간</label>
                                    <div className="expiry-date-group">
                                        <div className='inp-wrapper'>
                                            <input
                                                type="text"
                                                id="expiryMonth"
                                                name="expiryMonth"
                                                value={cardInfo.expiryMonth}
                                                onChange={(e) => handleExpiryDateChange(e, expiryYearRef)}
                                                onBlur={handleBlur}
                                                maxLength="2"
                                                ref={expiryMonthRef}
                                                className={errors.expiryMonth ? 'is-invalid' : ''}
                                            />
                                            <span>월</span>
                                        </div>
                                        <span>/</span>
                                        <div className='inp-wrapper'>
                                            <input
                                                type="text"
                                                id="expiryYear"
                                                name="expiryYear"
                                                value={cardInfo.expiryYear}
                                                onChange={(e) => handleExpiryDateChange(e, cscRef)}
                                                onBlur={handleBlur}
                                                maxLength="2"
                                                ref={expiryYearRef}
                                                className={errors.expiryYear ? 'is-invalid' : ''}
                                            />
                                            <span>년</span>
                                        </div>
                                    </div>
                                {(errors.expiryMonth || errors.expiryYear) && (
                                    <div className="invalid-feedback" style={{display: 'block'}}>
                                        {errors.expiryMonth || errors.expiryYear}
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="form-group">
                                <div className="form-group">
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                        <label htmlFor="csc">보안 코드</label>
                                        <InfoButton placement="" offsetY={8}>
                                            <ul style={{fontSize:'14px', fontWeight:'400', lineHeight:'140%'}}>
                                                <li>Visa, Mastercard: 카드 뒷면 서명란 옆의 3자리 숫자를 입력해 주세요.</li>
                                                <li>American Express: 카드 앞면 카드번호 위쪽의 4자리 숫자를 입력해 주세요.</li>
                                            </ul>
                                        </InfoButton>
                                    </div>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{3,4}"
                                        autoComplete="cc-csc"
                                        id="csc"
                                        name="csc"
                                        value={cardInfo.csc}
                                        onChange={handleCscChange}
                                        onBlur={handleBlur}
                                        maxLength={mappedCardType === 'AMX' ? 4 : 3}
                                        ref={cscRef}
                                        className={errors.csc ? 'is-invalid' : ''}
                                    />
                                    {errors.csc && <div className="invalid-feedback">{errors.csc}</div>}
                                </div>
                            </div>
                        </div>
                        <div className='content payment-before'>
                            <ul>
                                <li><div className='dot'><DotIcon/></div> 해외 승인 가능한 신용카드로 결제 가능합니다.</li>
                                <li><div className='dot'><DotIcon/></div> 크루즈 본사 시스템을 통해 해외 결제가 이루어집니다.</li>
                                <li><div className='dot'><DotIcon/></div> 해외 결제 시 신용카드사에 따라 수수료가 부과될 수 있습니다.</li>
                            </ul>
                        </div>
                    </div>
                    <div className='payment-content'>
                        <h2>주의사항</h2>
                        <div className='content caution' style={{}}>
                            {renderCautionItems(cautionItems)}
                        </div>
                    </div>
                    <div className='payment-content'>
                        <h2>{specialTerms.title || '특별 약관'}</h2>
                        <div className='content terms' style={{}}>
                            {specialTerms.description && (
                                <p>{specialTerms.description}</p>
                            )}
                            {renderTermItems(specialTerms.items || [], 'terms-contents')}
                            {specialTerms.extra?.type === 'link' && (
                                <CommonModal
                                    trigger={<button type='button' className='detail-terms-modal'>{specialTerms.extra.text || '상세 약관 보기＞'}</button>}
                                    title="상세 약관"
                                    showFooter={false}
                                >
                                    상세 약관 내용입니다.
                                </CommonModal>
                            )}
                        </div>
                    </div>
                    <div className='payment-content'>
                        <h2 ref={cancellationTermsRef} tabIndex={-1} className='payment-cancellation-title'>{cancelTerms.title || '취소료 규정'}</h2>
                        <div className='content cancle-fee-regulation'>
                            {renderTermItems(cancelTerms.items || [], 'cancle-fee')}
                            {cancelTerms.extra?.type === 'notes' && Array.isArray(cancelTerms.extra.notes) && cancelTerms.extra.notes.length > 0 && (
                                <div className='info'>
                                    {cancelTerms.extra.notes.map((note, index) => (
                                        <p key={`cancel-note-${index}`}>{note}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                    </div>
                </div>

                {/* 결제 정보 및 sticky 효과 */}
                <div className='guest-payment-price'>
                    <div className='guest-price-info'>
                        <div className='price-header'>
                            <h2>{cabinName}</h2>
                            {userSelectCabinInfo && (
                                <div>
                                    층수 {userSelectCabinInfo?.deckname?.split(' ')[1]}F <span className='divider' /> 선실번호 {userSelectCabinInfo?.cabinno} <span className='divider' /> {userSelectCabinInfo?.location}
                                </div>
                            )}
                            <div>
                                {PASSENGER_TYPE_ORDER
                                    .map(type => ({ type, count: Number(counts?.[type] || 0) }))
                                    .filter(item => item.count > 0)
                                    .map((item, index, arr) => (
                                        <React.Fragment key={item.type}>
                                            <span>{ageLabelMap[item.type]} {item.count}</span>
                                            {index < arr.length - 1 && <span className="divider" />}
                                        </React.Fragment>
                                    ))}
                            </div>
                        </div>

                        <div className="price-body">
                            {passengerMappingWarning && <p role="alert">{passengerMappingWarning}</p>}
                            {(!Array.isArray(perPerson) && passengersList.length === 0) ? (
                                <div>가격 정보를 불러오는 중입니다...</div>
                            ) : (
                                passengersList.map(pax => {
                                    const guestNo = pax.guestNo;
                                    const priceDetails = passengerPriceDetails[guestNo] || [];
                                    const benefitDetails = passengerBenefitDetails[guestNo] || [];
                                    const paxLabel = ageLabelMap[pax?.type] || '탑승객';
                                    const totalByPax = priceDetails.reduce((sum, item) => sum + (Number(item?.price) || 0), 0);
                                    return (
                                        <Accordion key={`guest-${guestNo}`} flush>
                                            <Accordion.Item eventKey={`guest-${guestNo}`}>
                                                <Accordion.Header>
                                                    <div>
                                                        {paxLabel} {pax?.typeIndex || guestNo} <ArrowCircleIcon />
                                                    </div>
                                                    <p>{Number(totalByPax || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    {priceDetails.map((item, i) => {
                                                        const desc = getTraveltekPriceLabel(item, priceTranslations);
                                                        const amt = Number(item?.price) || 0;
                                                        return (
                                                            <div key={i} className='detail-price-info'>
                                                                <span>{desc}</span>
                                                                <span>{amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    {benefitDetails.length > 0 && (
                                                        <div className='benefit-price-group'>
                                                            <div className='benefit-price-head'>
                                                                <span className='benefit-price-title'>특별 혜택</span>
                                                                <span className='benefit-price-note'>선내에서 사용 가능한 무료 리워드 크레딧</span>
                                                            </div>
                                                            {benefitDetails.map((item, i) => {
                                                                const categoryKey = (item?.category || '').toString().toLowerCase();
                                                                const desc = getTraveltekPriceLabel(item, priceTranslations,
                                                                    categoryKey === 'onboardcredit' ? 'ON-BOARD CREDIT' : undefined);
                                                                const amt = Number(item?.price) || 0;
                                                                return (
                                                                    <div key={`benefit-${i}`} className='detail-price-info benefit-price-info'>
                                                                        <span>{desc}</span>
                                                                        <span>{amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {priceDetails.length === 0 && benefitDetails.length === 0 && (
                                                        <div className='detail-price-info'>
                                                            <span>가격 정보 없음</span>
                                                            <span>-</span>
                                                        </div>
                                                    )}
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    );
                                })
                            )}
                        </div>

                        <div className='price-footer'>
                            <PromotionDiscountRows
                                paymentSummary={reservationCreatePaymentSummary}
                                promotion={reservationCreateResult?.applied_promotion}
                                formatCurrency={formatUsdCurrency}
                            />
                            <div className='total-price-wrapper'>
                                <span>총 요금</span>
                                <span className='total-price'>{formatUsdCurrency(totalPriceCalculated)}</span>
                            </div>
                            {showPaymentBreakdown && (
                                <div className='payment-breakdown'>
                                    <div className='payment-line'>
                                        <span className='label'>예약금</span>
                                        <span className='amount'>{formatUsdCurrency(depositAmountCalculated)}</span>
                                        <span className='status'>즉시 결제</span>
                                    </div>
                                    <div className='payment-line'>
                                        <span className='label'>잔금</span>
                                        <span className='amount'>{formatUsdCurrency(finalAmountCalculated)}</span>
                                        <span className='status'>{formatDueDateStatus(finalDueDateCalculated)}</span>
                                    </div>
                                </div>
                            )}

                            {!isMobile && paymentButtonContent}
                        </div>

                    </div>
                </div>
            </div>
            {isMobile && portalContainer && ReactDOM.createPortal(paymentButtonContent, portalContainer)}
            {/* 모바일에서는 footer 외부에 생성한 포털에 결제 버튼을 꽂아 고정 배치한다. */}
        </>
    )
}

export default GuestPayment;
